import { getSafeRedirectPath } from '../utils/safeRedirect';

export const CODE_LENGTH = 6;

export const emptyCode = () => Array(CODE_LENGTH).fill('');

export const isCodeComplete = (digits) => digits.length === CODE_LENGTH && digits.every((d) => /^\d$/.test(d));

export const fillCodeDigits = (digits, index, raw) => {
  let chars = String(raw || '').replace(/\D/g, '').split('');
  const next = [...digits];
  if (chars.length === 0) {
    next[index] = '';
    return { digits: next, focus: index };
  }
  if (chars.length === 2 && next[index]) {
    chars = [chars[0] === next[index] ? chars[1] : chars[0]];
  }
  const start = chars.length >= CODE_LENGTH ? 0 : index;
  let i = start;
  for (const c of chars) {
    if (i >= CODE_LENGTH) break;
    next[i] = c;
    i += 1;
  }
  return { digits: next, focus: Math.min(i, CODE_LENGTH - 1) };
};

export const isAlreadyConfirmedError = (error) => error?.name === 'NotAuthorizedException' && /status is confirmed/i.test(error?.message || '');

export const confirmCodeErrorMessage = (error) => {
  switch (error?.name) {
    case 'CodeMismatchException':
      return 'That code is not right. Check the latest email from Spiggl and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Tap "resend code" to get a new one.';
    case 'LimitExceededException':
    case 'TooManyFailedAttemptsException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'UserNotFoundException':
      return 'We could not find this sign-up. Please start again.';
    default:
      return error?.message || 'Could not verify the code. Please try again.';
  }
};

export const resendCodeErrorMessage = (error) => {
  switch (error?.name) {
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many codes requested. Please wait a few minutes and try again.';
    case 'InvalidParameterException':
      return 'This email is already confirmed. Sign in instead.';
    default:
      return error?.message || 'Could not send a new code. Please try again.';
  }
};

export const signInErrorMessage = (error) => {
  switch (error?.name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'UserNotConfirmedException':
      return 'Your email is not confirmed yet. Enter the code we emailed you, or get a new one.';
    case 'AccountDeletedError':
      return 'This account was deleted.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many failed attempts. Please try again later.';
    default:
      return error?.message || "Couldn't log you in. Check your email and password.";
  }
};

export const signUpErrorMessage = (error) => {
  switch (error?.name) {
    case 'InvalidPasswordException':
      return 'Password must be at least 8 characters with uppercase, lowercase, and number.';
    case 'InvalidParameterException':
      return 'Please check your email address and password.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    default:
      return error?.message || 'Failed to sign up. Please try again.';
  }
};

export const passwordChangeErrorMessage = (error) => {
  switch (error?.name) {
    case 'NotAuthorizedException':
      return 'Your current password is incorrect.';
    case 'InvalidPasswordException':
      return 'New password must be at least 8 characters with uppercase, lowercase, and number.';
    case 'InvalidParameterException':
      return 'Please check both passwords and try again.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'UserUnAuthenticatedException':
    case 'UserNotFoundException':
      return 'Your session has ended. Please sign in again.';
    default:
      return 'Failed to update password. Please try again.';
  }
};

export const isFederatedAttributes = (attributes) => {
  const raw = attributes?.identities;
  if (!raw) return false;
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.length > 0 : !!list;
  } catch {
    return true;
  }
};

const RETURN_KEY = 'previousPage';
export const RETURN_PATH_TTL_MS = 15 * 60 * 1000;

export const parseReturnPath = (raw, now = Date.now()) => {
  if (!raw) return null;
  let saved;
  try {
    saved = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!saved || typeof saved !== 'object' || typeof saved.at !== 'number') return null;
  if (now - saved.at > RETURN_PATH_TTL_MS || saved.at > now + 60000) return null;
  return getSafeRedirectPath(saved.path);
};

export const rememberReturnPath = (path, now = Date.now()) => {
  try {
    sessionStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: now }));
  } catch {
  }
};

export const clearReturnPath = () => {
  try {
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
  }
};

export const takeReturnPath = (now = Date.now()) => {
  let raw = null;
  try {
    raw = sessionStorage.getItem(RETURN_KEY);
  } catch {
    return null;
  }
  clearReturnPath();
  return parseReturnPath(raw, now);
};

const RESUME_EXEMPT = ['/create-profile', '/terms', '/privacy', '/forgot-password', '/support', '/contact'];

export const shouldRedirectToResume = (pathname) => !RESUME_EXEMPT.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export const resumeUrl = ({ tab, claim } = {}) => {
  const params = new URLSearchParams({ resume: '1' });
  if (tab === 'user' || tab === 'company') params.set('tab', tab);
  if (claim) {
    params.set('tab', 'company');
    params.set('claim', claim);
  }
  return `/create-profile?${params.toString()}`;
};

export const PERMANENT_PROFILE_ERRORS = ['INVALID_INPUT', 'HANDLE_INVALID', 'HANDLE_RESERVED', 'ACCOUNT_DELETED', 'EMAIL_IN_USE', 'PROFILE_EXISTS', 'BAD_KIND'];

export const isUnauthorizedGraphqlError = (error) => {
  const errors = error?.errors || [];
  return errors.some((e) => e?.errorType === 'Unauthorized' || /not authorized/i.test(e?.message || ''));
};

export const isUnknownArgumentError = (error) => {
  const errors = error?.errors || [];
  return errors.some((e) => /unknown (field )?argument|ageConfirmed/i.test(e?.message || '') && /validation|argument/i.test(`${e?.errorType || ''} ${e?.message || ''}`));
};

export const profileErrorMessage = (error) => {
  if (error?.code === 'EMAIL_IN_USE') return 'This email already has a Spiggl account. Sign out, then sign in with your email and password.';
  if (error?.code === 'PROFILE_EXISTS') return 'This login already has a Spiggl profile. Sign out to create a different one.';
  if (PERMANENT_PROFILE_ERRORS.includes(error?.code) || error?.code === 'RATE_LIMITED' || error?.code === 'HANDLE_TAKEN') {
    return error.message || 'We could not create your profile. Please try again.';
  }
  if (['UserNotConfirmedException', 'NotAuthorizedException', 'UserNotFoundException', 'AccountDeletedError'].includes(error?.name)) {
    return signInErrorMessage(error);
  }
  return 'Your account is ready, but we could not finish your profile. Please try again.';
};

export const AGE_ERROR = 'You must be 18 or older to use Spiggl.';

export const summarizeOpenInvoices = (invoices, isPaid) => {
  const list = (invoices || []).filter(Boolean);
  const open = list.filter((inv) => !isPaid(inv));
  const disputed = list.filter((inv) => inv.disputed === true);
  return { open: open.length, disputed: disputed.length, total: list.length };
};

export const invoiceWarning = ({ open, disputed }) => {
  const parts = [];
  if (open > 0) parts.push(`${open} open invoice${open === 1 ? '' : 's'} that customers have not finished paying`);
  if (disputed > 0) parts.push(`${disputed} disputed payment${disputed === 1 ? '' : 's'}`);
  if (!parts.length) return '';
  return `You have ${parts.join(' and ')}. If you delete your profile, customers can no longer pay you through Spiggl and you cannot respond from your account. Money already paid still goes to your bank through Stripe.`;
};
